// DJSEM platform adapter.
//
// The DJSEM workspace UI (the shell + all modules) is authored ONCE and runs on two hosts:
//   - Electron desktop (apps/mvp-electron), where a preload bridge exposes `window.djsemLive`
//     for live-bridge health + local package cache, and `window.djsemApiBase` for the API base;
//   - the browser (apps/web), where none of those globals exist.
//
// Rather than let the renderer reach for Electron-only globals directly (which would make it
// crash or silently mis-behave on the web), it consumes this adapter. Each host provides a
// `DJSEMPlatform`; the renderer never branches on "am I in Electron?" itself. This is what lets
// the same renderer run 1:1 on web — the web platform simply reports "no live bridge" and
// resolves the API base from the environment instead of a preload global.

/** Point-in-time health of the desktop live bridge (beat-link adapter connection + cloud reachability). */
export interface DJSEMLiveHealth {
  bridgeConnected: boolean;
  bridgeHealthy: boolean;
  lastHeartbeatAt: number | null;
  cloudReachable: boolean;
  updatedAt: number;
}

/** A locally-cached package entry (desktop-only local package cache). */
export interface DJSEMCachedPackage { trackId: string; }

/** Live-bridge surface. On web this is a null-object: no bridge, empty cache, cloud assumed reachable. */
export interface DJSEMLiveBridge {
  /** true only on hosts that actually have a desktop live bridge (Electron). */
  readonly available: boolean;
  getHealth(): Promise<DJSEMLiveHealth>;
  listCached(): Promise<DJSEMCachedPackage[]>;
  /** Subscribe to health updates; returns an unsubscribe fn. No-op on web. */
  onHealth(cb: (h: DJSEMLiveHealth) => void): () => void;
  /** Subscribe to normalized DJSEMLiveEvent envelopes forwarded from the beat-link bridge; returns an unsubscribe fn. No-op on web. */
  onLiveEvent(cb: (envelope: unknown) => void): () => void;
}

/** Parameters for a desktop-only local video render via {@link DJSEMLocalFileBridge.renderVideo}. */
export interface DJSEMRenderVideoRequest {
  audioPath: string;
  outputType: string;
  aspectRatio: string;
  durationMs: number;
  format: "mp4" | "mov" | "webm";
  qualityMode: "draft" | "standard" | "high";
}

/** Result of a local render: either the output file path, or a fail-closed reason. */
export type DJSEMRenderVideoResult = { ok: true; outputPath: string } | { ok: false; error: string };

/**
 * Local-filesystem surface: picking a real audio file and rendering a real video from it.
 * Desktop-only — the real renderer (@djsem/ai-video-renderer) needs a literal local filesystem
 * path and native ffmpeg/canvas, neither of which a browser has. On web this is a null-object:
 * unavailable, pick returns null, render always fails closed with an honest reason.
 */
export interface DJSEMLocalFileBridge {
  /** true only on hosts that actually have local file + render access (Electron). */
  readonly available: boolean;
  pickAudioFile(): Promise<{ path: string; name: string; sizeBytes: number } | null>;
  renderVideo(input: DJSEMRenderVideoRequest): Promise<DJSEMRenderVideoResult>;
  revealInFolder(path: string): void;
}

/** @returns A {@link DJSEMLocalFileBridge} that reports unavailable and fails every operation closed (web/no-bridge fallback). */
export function nullLocalFileBridge(): DJSEMLocalFileBridge {
  return {
    available: false,
    async pickAudioFile() { return null; },
    async renderVideo() { return { ok: false, error: "Local rendering is a desktop feature — it needs native ffmpeg, which the browser doesn't have." }; },
    revealInFolder() { /* no local filesystem to reveal on web */ },
  };
}

/** A verified, not-yet-expired offline entitlement cache entry (real plan/credits, from a signed token). */
export interface DJSEMCachedEntitlement { planLevel: string; renderCreditsRemaining: number; expiresAt: string }

/**
 * Offline desktop entitlement cache (task #45, Phase C): lets the Electron app keep honoring a
 * signed-in user's real plan/render credits when `/api/me` can't be reached (typical at a venue
 * with no internet), instead of failing closed to the free tier. Desktop-only — the browser has
 * no offline mode to speak of (a lost connection just means a lost connection). See
 * @djsem/shared's entitlementToken.ts for why this is a signed cache of the account's real
 * entitlement, NOT a separate perpetual-license-key model.
 */
export interface DJSEMOfflineEntitlementBridge {
  readonly available: boolean;
  /** Cache a freshly-fetched signed token (call this after a successful /api/me + token fetch while online). */
  cacheToken(token: unknown): Promise<void>;
  /** The cached entitlement, ONLY if its signature verifies and it hasn't expired. Null otherwise — never a stale/invalid fallback. */
  getCached(): Promise<DJSEMCachedEntitlement | null>;
}

/** @returns A {@link DJSEMOfflineEntitlementBridge} that has no cache (web fallback — no offline mode). */
export function nullOfflineEntitlementBridge(): DJSEMOfflineEntitlementBridge {
  return {
    available: false,
    async cacheToken() { /* no offline cache on web */ },
    async getCached() { return null; },
  };
}

export interface DJSEMCachedLicense { licenseId: string; tier: string }

/**
 * Perpetual desktop license (task #45, Phase E). Desktop replaces recurring subscription
 * billing entirely with a one-time-purchase perpetual license — this is the PRIMARY source of
 * desktop feature-tier entitlement, not an offline fallback like `offlineEntitlement` above.
 * Web is unaffected (null-object here): it keeps the account/subscription system for its
 * cloud-dependent features. Same shape/division-of-labor as `offlineEntitlement`: this bridge
 * only owns the machine id + local disk cache; the actual HTTP calls (purchase, activate,
 * revocation-check) go through DJSEMApiClient, same as every other network call in the app.
 * See docs/state/agents/claude/prompts/147-*.md for the full design.
 */
export interface DJSEMLicenseBridge {
  readonly available: boolean;
  /** Stable per-install id, generated once and persisted — needed before activation. */
  getMachineId(): Promise<string>;
  /** Cache a freshly-issued signed perpetual license token (call after a successful activate). */
  cacheToken(token: unknown): Promise<void>;
  /** The cached license, ONLY if its signature verifies and it's bound to this machine. Null otherwise — never a stale/invalid fallback. */
  getCached(): Promise<DJSEMCachedLicense | null>;
  /** Clears the local cache — call after an online revocation check reveals the license is no longer valid. */
  clearCached(): Promise<void>;
}

/** @returns A {@link DJSEMLicenseBridge} that has no license concept (web fallback). */
export function nullLicenseBridge(): DJSEMLicenseBridge {
  return {
    available: false,
    async getMachineId() { return ""; },
    async cacheToken() { /* no license concept on web */ },
    async getCached() { return null; },
    async clearCached() { /* no license concept on web */ },
  };
}

export type DJSEMComputeTier = "cuda_gpu" | "apple_silicon_gpu" | "cpu";
export type DJSEMComputeTierPreference = "auto" | DJSEMComputeTier;
export interface DJSEMHardwareProbeResult {
  platform: string; arch: string; cudaAvailable: boolean; appleSiliconGpuAvailable: boolean; probedAt: string;
}
export interface DJSEMComputeCapabilityReport {
  probe: DJSEMHardwareProbeResult;
  availableTiers: DJSEMComputeTier[];
  preference: DJSEMComputeTierPreference;
  tier: DJSEMComputeTier;
  usedFallback: boolean;
  warnings: string[];
}
export interface DJSEMComputeSettings { preference: DJSEMComputeTierPreference; updatedAt: string }

/**
 * Local compute-tier capability + preference (prompt 149). Desktop-only — a browser has no
 * GPU/process probing surface, and web never runs these local models at all. Web is a
 * null-object: unavailable, no fake capability report.
 */
export interface DJSEMComputeBridge {
  readonly available: boolean;
  getCapability(): Promise<DJSEMComputeCapabilityReport | null>;
  getPreference(): Promise<DJSEMComputeSettings | null>;
  setPreference(preference: DJSEMComputeTierPreference): Promise<DJSEMComputeSettings | null>;
}

export function nullComputeBridge(): DJSEMComputeBridge {
  return {
    available: false,
    async getCapability() { return null; },
    async getPreference() { return null; },
    async setPreference() { return null; },
  };
}

/**
 * The single adapter surface the shared renderer consumes instead of branching on host. One
 * implementation per host ({@link createWebPlatform} / {@link createElectronPlatform}); the
 * renderer never checks "am I in Electron?" itself.
 */
export interface DJSEMPlatform {
  /** "electron" | "web" — for copy/telemetry only; behaviour comes from the capabilities below. */
  readonly kind: "electron" | "web";
  /** Absolute base URL of the DJSEM API (worker). */
  readonly apiBase: string;
  readonly live: DJSEMLiveBridge;
  readonly files: DJSEMLocalFileBridge;
  readonly offlineEntitlement: DJSEMOfflineEntitlementBridge;
  readonly license: DJSEMLicenseBridge;
  readonly compute: DJSEMComputeBridge;
}

/** A live bridge that reports "nothing here" — used on web and as an Electron fallback. */
export function nullLiveBridge(): DJSEMLiveBridge {
  return {
    available: false,
    async getHealth(): Promise<DJSEMLiveHealth> {
      return { bridgeConnected: false, bridgeHealthy: false, lastHeartbeatAt: null, cloudReachable: true, updatedAt: Date.now() };
    },
    async listCached(): Promise<DJSEMCachedPackage[]> {
      return [];
    },
    onHealth(): () => void {
      return () => { /* no live bridge to unsubscribe from */ };
    },
    onLiveEvent(): () => void {
      return () => { /* no live bridge to unsubscribe from */ };
    },
  };
}

/**
 * Build the web platform. The API base is resolved from (in priority order):
 *   1. an explicit override (e.g. Vite's `import.meta.env.VITE_API_BASE`), passed by the caller;
 *   2. same-origin `/api` in production web builds (the worker is deployed behind the same host);
 *   3. the local worker dev default.
 * The web host has no live bridge.
 */
export function createWebPlatform(opts?: { apiBase?: string; sameOrigin?: boolean }): DJSEMPlatform {
  const apiBase = resolveWebApiBase(opts);
  return {
    kind: "web", apiBase, live: nullLiveBridge(), files: nullLocalFileBridge(),
    offlineEntitlement: nullOfflineEntitlementBridge(), license: nullLicenseBridge(),
    compute: nullComputeBridge(),
  };
}

/**
 * Resolve the web platform's API base URL from an explicit override, same-origin, or the local
 * worker dev default, in that priority order.
 * @param opts - `apiBase` explicit override; `sameOrigin` to use `window.location.origin`.
 * @returns The resolved base URL with no trailing slash.
 */
export function resolveWebApiBase(opts?: { apiBase?: string; sameOrigin?: boolean }): string {
  if (opts?.apiBase && opts.apiBase.length > 0) return stripTrailingSlash(opts.apiBase);
  if (opts?.sameOrigin && typeof window !== "undefined" && window.location?.origin) {
    return stripTrailingSlash(window.location.origin);
  }
  return "http://localhost:8787";
}

/**
 * Build the Electron platform from the preload-exposed globals. If the bridge is missing (e.g.
 * a broken preload), it degrades to the null bridge so the shell still renders rather than
 * throwing — the shell will just show the bridge as disconnected.
 */
export function createElectronPlatform(win: {
  djsemApiBase?: string;
  djsemLive?: {
    getHealth: () => Promise<DJSEMLiveHealth>;
    listCached: () => Promise<DJSEMCachedPackage[]>;
    onHealth: (cb: (h: DJSEMLiveHealth) => void) => () => void;
    onLiveEvent: (cb: (envelope: unknown) => void) => () => void;
  };
  djsemFiles?: {
    pickAudioFile: () => Promise<{ path: string; name: string; sizeBytes: number } | null>;
    renderVideo: (input: DJSEMRenderVideoRequest) => Promise<DJSEMRenderVideoResult>;
    revealInFolder: (path: string) => void;
  };
  djsemEntitlement?: {
    cacheToken: (token: unknown) => Promise<{ ok: boolean }>;
    getCached: () => Promise<DJSEMCachedEntitlement | null>;
  };
  djsemLicense?: {
    getMachineId: () => Promise<string>;
    cacheToken: (token: unknown) => Promise<{ ok: boolean }>;
    getCached: () => Promise<DJSEMCachedLicense | null>;
    clearCached: () => Promise<{ ok: boolean }>;
  };
  djsemCompute?: {
    getCapability: () => Promise<DJSEMComputeCapabilityReport>;
    getPreference: () => Promise<DJSEMComputeSettings>;
    setPreference: (preference: DJSEMComputeTierPreference) => Promise<DJSEMComputeSettings>;
  };
}): DJSEMPlatform {
  const apiBase = stripTrailingSlash(win.djsemApiBase || "http://localhost:8787");
  const bridge = win.djsemLive;
  const live: DJSEMLiveBridge = bridge
    ? {
        available: true,
        getHealth: () => bridge.getHealth(),
        listCached: () => bridge.listCached(),
        onHealth: (cb) => bridge.onHealth(cb),
        onLiveEvent: (cb) => bridge.onLiveEvent(cb),
      }
    : nullLiveBridge();
  const filesBridge = win.djsemFiles;
  const files: DJSEMLocalFileBridge = filesBridge
    ? {
        available: true,
        pickAudioFile: () => filesBridge.pickAudioFile(),
        renderVideo: (input) => filesBridge.renderVideo(input),
        revealInFolder: (path) => filesBridge.revealInFolder(path),
      }
    : nullLocalFileBridge();
  const entitlementBridge = win.djsemEntitlement;
  const offlineEntitlement: DJSEMOfflineEntitlementBridge = entitlementBridge
    ? {
        available: true,
        cacheToken: async (token) => { await entitlementBridge.cacheToken(token); },
        getCached: () => entitlementBridge.getCached(),
      }
    : nullOfflineEntitlementBridge();
  const licenseBridge = win.djsemLicense;
  const license: DJSEMLicenseBridge = licenseBridge
    ? {
        available: true,
        getMachineId: () => licenseBridge.getMachineId(),
        cacheToken: async (token) => { await licenseBridge.cacheToken(token); },
        getCached: () => licenseBridge.getCached(),
        clearCached: async () => { await licenseBridge.clearCached(); },
      }
    : nullLicenseBridge();
  const computeBridge = win.djsemCompute;
  const compute: DJSEMComputeBridge = computeBridge
    ? {
        available: true,
        getCapability: () => computeBridge.getCapability(),
        getPreference: () => computeBridge.getPreference(),
        setPreference: (preference) => computeBridge.setPreference(preference),
      }
    : nullComputeBridge();
  return { kind: "electron", apiBase, live, files, offlineEntitlement, license, compute };
}

function stripTrailingSlash(s: string): string {
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

/**
 * Derive shell status flags from live-bridge health + whether a live bridge exists at all.
 *
 * This is the parity-critical rule: on WEB there is no live bridge, so a null/absent health must
 * NOT read as "offline" (the cloud API is reachable independently of any desktop bridge). Only a
 * host that actually HAS a live bridge can report the cloud unreachable or safe-mode active.
 *
 * Pure + exported so web/desktop status behaviour is unit-tested rather than living inline in JSX.
 */
export function deriveShellOnlineStatus(input: {
  liveAvailable: boolean;
  health: DJSEMLiveHealth | null;
}): { cloudOnline: boolean; safeModeActive: boolean } {
  if (!input.liveAvailable) {
    // Web (or an Electron host with no bridge bound): cloud is reachable, safe mode is N/A.
    return { cloudOnline: true, safeModeActive: false };
  }
  return {
    cloudOnline: input.health?.cloudReachable ?? false,
    safeModeActive: !(input.health?.bridgeHealthy ?? false),
  };
}
